import { getSignedMediaUrl } from "@/lib";
import React from 'react';
import { motion, AnimatePresence } from "motion/react";
import { X } from 'lucide-react';
import { createPortal } from 'react-dom';

interface TransferAddOnModalProps {
    isOpen: boolean;
    onClose: () => void;
    addon: any;
}

export const TransferAddOnModal: React.FC<TransferAddOnModalProps> = ({ isOpen, onClose, addon }) => {
    if (!isOpen || !addon) return null;

    const imageUrl = addon.add_on?.image_id ? getSignedMediaUrl(addon.add_on.image_id, 'image').url : null;

    return createPortal(
        <AnimatePresence>
            <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="fixed inset-0 z-[100] flex items-center justify-center bg-black/80 backdrop-blur-sm p-4"
                onClick={onClose}
            >
                <motion.div
                    initial={{ opacity: 0, scale: 0.95, y: 20 }}
                    animate={{ opacity: 1, scale: 1, y: 0 }}
                    exit={{ opacity: 0, scale: 0.95, y: 20 }}
                    className="relative w-full max-w-md bg-gray-900 rounded-2xl border border-gray-700/50 overflow-hidden shadow-2xl flex flex-col"
                    onClick={(e) => e.stopPropagation()}
                    style={{
                        background: 'linear-gradient(135deg, rgba(17, 24, 39, 0.98), rgba(31, 41, 55, 0.98))',
                    }}
                >
                    <button
                        onClick={onClose}
                        className="absolute top-4 right-4 z-10 p-2 bg-black/50 hover:bg-black/70 rounded-full text-white transition"
                    >
                        <X className="w-4 h-4" />
                    </button>

                    {/* Image Header */}
                    <div className="relative h-48 overflow-hidden">
                        <div
                            className="absolute inset-0 bg-cover bg-center"
                            style={{
                                backgroundImage: imageUrl ? `url(${imageUrl})` : 'linear-gradient(135deg, #1f2937, #374151)'
                            }}
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/40 to-transparent" />
                        <div className="absolute bottom-4 left-4 right-4">
                            <h3 className="text-xl font-bold text-white">{addon.add_on?.name || 'Add-on'}</h3>
                        </div>
                    </div>

                    <div className="p-6 space-y-4">
                        {/* Description */}
                        {addon.add_on?.description && (
                            <div>
                                <h4 className="text-sm font-semibold text-gray-400 uppercase mb-2">Description</h4>
                                <p className="text-gray-300 text-sm leading-relaxed">
                                    {addon.add_on.description}
                                </p>
                            </div>
                        )}

                        {/* Breakdown (No Price) */}
                        <div className="bg-white/5 rounded-xl p-4 border border-white/10">
                            <h4 className="text-sm font-bold text-white mb-3">Details</h4>
                            <div className="flex justify-between items-center text-sm">
                                <span className="text-gray-400">Total Quantity</span>
                                <span className="text-white font-bold">{addon.quantity}</span>
                            </div>
                            <div className="mt-3 pt-3 border-t border-white/10">
                                <div className="flex gap-2">
                                    <span className="px-2 py-1 bg-violet-500/20 text-violet-300 rounded text-xs border border-violet-500/30">
                                        Included with Transfer
                                    </span>
                                </div>
                            </div>
                        </div>
                    </div>
                </motion.div>
            </motion.div>
        </AnimatePresence>,
        document.body
    );
};